<template>
  <div class="FieldFillable">
    <TabsHead
      v-if="variants.length"
      :indexActive="tabIndex"
      :labels="tabsLabels"
      @update:indexActive="setTabIndex">
      <template #tabSuffix="{ isActive, index }">
        <div
          v-if="index === variants.length && isGenerating"
          class="loaderWrap">
          <Loader />
        </div>
        <Icon
          v-else-if="variants.length > 1 && isActive"
          name="cross"
          @click="removeVariant(variantIndex)" />
      </template>
    </TabsHead>
    <!--
    <Redundancies
      :redundancies.sync="redundancies[variantIndex]"
      :text="variantFocused" />
    -->
    <div v-if="isResearch">
      <slot name="research" />
    </div>
    <div v-else>
      <Markdown
        :key="variantIndex"
        ref="field"
        :value="variantFocused"
        @update:value="setVariantFocused" />
      <div class="buttons">
        <Btn
          class="sm info ib"
          :disabled="disabled"
          :isLoading="isGenerating"
          @click="continueVariantFocused">
          {{ isEmpty ? $t('fill') : $t('continue') }}
        </Btn>
        <Btn
          v-if="!isEmpty"
          class="sm success ib"
          :isLoading="isGenerating"
          @click="decorateVariantFocused">
          {{ $t('decorate') }}
        </Btn>
      </div>
    </div>
  </div>
</template>

<script>
import { pluck, update } from 'rambda'
import Markdown from '@components/fields/Markdown'
// import Redundancies from './Redundancies'
import TabsHead from '@components/TabsHead'
import { actions } from '@utils'
import { removeIndex } from '@utils/collections'

const ADD_LABEL = '+'

export default {
  components: { Markdown, TabsHead },
  props: {
    article: Object,
    disabled: Boolean,
    filler: { type: Function, required: true },
    type: String,
    variantIndex: { type: Number, required: true },
    variants: { type: Array, required: true },
  },
  data() {
    return {
      isGenerating: false,
      isResearch: true,
      tabIndex: 0,
      redundancies: [],
    }
  },
  computed: {
    isEmpty() {
      return !this.variantFocused || !this.variantFocused.length
    },
    variantFocused() {
      return this.variants[this.variantIndex]
    },
    tabsLabels() {
      const variantsLabels = this
        .variants
        .map((_, index) => this.$t('variant', [index + 1]))

      return [
        this.$t('research'),
        ...variantsLabels,
        ADD_LABEL,
      ]
    },
  },
  methods: {
    async addVariant() {
      if (this.isGenerating) return

      const { text, redundancy } = await this.generateVariant(null)

      this.$set(this.redundancies, this.variants.length, redundancy)
      this.setVariants([
        ...(this.variants || []),
        text,
      ])
    },
    async generateVariant(text) {
      return (await this
        .$wrap(this.filler(text), 'isGenerating'))
        .variants[0]
    },
    async continueVariantFocused() {
      const { text, redundancy } = await this
        .generateVariant(this.variantFocused.trim())

      const textJoined = [this.variantFocused, text]
        .filter(Boolean)
        .join('\n')

      this.setVariantFocused(textJoined)
      this.$set(this.redundancies, this.variantIndex, redundancy)
    },
    async decorateVariantFocused() {
      if (this.isGenerating) return

      const { images, links, text } = (await this
        .$wrap(this.CREATE_COMPLETION({
          type: 'TRANSFORM',
          promptTemplateName: 'DECORATIONS_ALL',
          category: this.article.category,
          fields: {
            topic: this.article.topic,
            text: this.variantFocused,
          },
        }), 'isGenerating'))
        .variants[0]

      this.$emit('update:links', links)
      this.$emit('update:imagePhrases', pluck('description', images))
      this.setVariantFocused(text)
    },
    async setToVariantIndex(variantIndex) {
      this.$emit('update:variantIndex', variantIndex)
    },
    removeVariant(index) {
      this.$emit('update:variantIndex', this.variantIndex - 1)
      this.setVariants(removeIndex(index, this.variants))
      this.$delete(this.redundancies, index)
    },
    setVariants(variants) {
      this.$emit('update:variants', variants)
    },
    setVariantFocused(text) {
      this.setVariants(update(this.variantIndex, text, this.variants))
    },
    async setTabIndex(tabIndex) {
      if (this.disabled) return

      const isResearch = !tabIndex

      this.tabIndex = tabIndex
      this.isResearch = isResearch

      if (isResearch) return

      if (this.tabsLabels.indexOf(ADD_LABEL) === tabIndex) {
        await this.addVariant()
      }

      if (tabIndex) this.setToVariantIndex(tabIndex - 1)
    },
    ...actions('CREATE_COMPLETION'),
  },
  watch: {
    variantFocused: {
      deep: true,
      async handler() {
        const { field } = this.$refs
        const isMarkdownFocused = [...(field.$el.children[0].children[2].classList)]
          .includes('CodeMirror-focused')

        if (isMarkdownFocused) return

        await this.$nextTick()
        this.$refs.field.refresh()
      },
    },
  },
  created() {
    if (!this.isEmpty) this.setTabIndex(this.variantIndex + 1)
  },
}
</script>

<style lang="scss">
.FieldFillable {
  position: relative;

  .TabsHead {
    margin-bottom: 0;

    .Tab {
      padding: 0 0.5rem;
    }

    .Icon {
      background: $neutral-light;
      border-radius: $radius-round;
      color: $text-color-mid;
      display: inline-block;
      font-size: $h7;
      height: 1.25rem;
      line-height: 1.3rem;
      margin-left: 0.5rem;
      text-align: center;
      width: 1.25rem;

      &:hover {
        background: $danger-light;
        color: $danger-dark;
      }
    }
  }

  .Tab {
    position: relative;

    .loaderWrap {
      align-items: center;
      background: rgba($white, 0.6);
      display: flex;
      height: 100%;
      left: 0;
      position: absolute;
      top: 0;
      width: 100%;
      z-index: 1;

      .Loader {
        height: 1.5rem;
        width: 1.5rem;
      }
    }
  }

  .buttons {
    bottom: 0.5rem;
    position: absolute;
    right: 0.5rem;
    z-index: 1;

    .Btn {
      margin-left: $grid-unit-half;
    }
  }
}
</style>

<i18n>
{
  "en": {
    "continue": "Continue",
    "decorate": "Decorate",
    "fill": "Write a draft",
    "research": "Research",
    "variant": "Variant #{0}"
  }
}
</i18n>
